import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { fmtDate } from "../lib/date";

const ACTIONS = [
  ["remind", "Remind recipient", "Only for a request that is still pending."],
  ["leave_pending", "Leave pending with follow-up date", "Re-appears here on the follow-up date."],
  ["cancel", "Cancel — current holder keeps it", "Custody does not change."],
  ["reassign", "Reassign to someone else", "Starts a new request and a new 7-day timer."],
  ["move_location", "Move to a location", "Only if it has physically been placed there."],
  ["confirm", "Confirm custody manually", "Admin override — reason is audited."],
];

function ResolveDialog({ req, onClose, onDone }) {
  const [action, setAction] = useState("");
  const [f, setF] = useState({ reason: "", follow_up_date: "", to_profile_id: "", location_id: "", profile_id: "", confirm_placed: false });
  const [people, setPeople] = useState([]);
  const [locs, setLocs] = useState([]);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  useEffect(() => {
    api.get("/asset-transfers/recipients").then((r) => setPeople(r.data)).catch(() => {});
    api.get("/config/locations").then((r) => setLocs(r.data.items || r.data || [])).catch(() => {});
  }, []);
  const avail = ACTIONS.filter(([k]) => req.status === "pending" || !["remind", "leave_pending"].includes(k));
  const submit = async () => {
    if (busy) return; setBusy(true);
    const body = { action, reason: f.reason };
    if (action === "leave_pending") body.follow_up_date = f.follow_up_date;
    if (action === "reassign") body.to_profile_id = f.to_profile_id;
    if (action === "move_location") { body.location_id = f.location_id; body.confirm_placed = f.confirm_placed; }
    if (action === "confirm" && f.profile_id) body.profile_id = f.profile_id;
    try { await api.post(`/asset-transfers/${req.id}/resolve`, body); toast.success("Resolved"); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  const sel = "h-10 border-2 border-slate-300 rounded px-2 text-sm";
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-lg" data-testid="resolve-dialog">
        <DialogHeader><DialogTitle className="font-display">{req.asset?.asset_number} — {req.asset?.name}</DialogTitle></DialogHeader>
        <div className="flex flex-col gap-1.5">
          {avail.map(([k, label, hint]) => (
            <label key={k} className={`flex items-start gap-2 border-2 rounded px-2 py-1.5 cursor-pointer ${action === k ? "border-indigo-500 bg-indigo-50" : "border-slate-200"}`}>
              <input type="radio" data-testid={`resolve-action-${k}`} checked={action === k} onChange={() => setAction(k)} className="mt-1" />
              <span><span className="text-sm font-semibold">{label}</span><span className="block text-xs text-slate-500">{hint}</span></span>
            </label>
          ))}
        </div>
        {action === "leave_pending" && <input type="date" data-testid="resolve-follow-up" className={sel} value={f.follow_up_date} onChange={(e) => set("follow_up_date", e.target.value)} />}
        {action === "reassign" && (
          <select data-testid="resolve-reassign-to" className={sel} value={f.to_profile_id} onChange={(e) => set("to_profile_id", e.target.value)}>
            <option value="">New recipient…</option>{people.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        )}
        {action === "move_location" && (<>
          <select data-testid="resolve-location" className={sel} value={f.location_id} onChange={(e) => set("location_id", e.target.value)}>
            <option value="">Existing location…</option>{locs.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" data-testid="resolve-confirm-placed" checked={f.confirm_placed} onChange={(e) => set("confirm_placed", e.target.checked)} />I confirm the asset has physically been placed at this location</label>
        </>)}
        {action === "confirm" && (
          <select data-testid="resolve-confirm-holder" className={sel} value={f.profile_id} onChange={(e) => set("profile_id", e.target.value)}>
            <option value="">Confirm holder: {req.to_name} (recipient)</option>{people.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        )}
        {action && <textarea data-testid="resolve-reason" rows={2} className="border-2 border-slate-300 rounded p-2 text-sm" placeholder={["cancel", "reassign", "confirm"].includes(action) ? "Reason (required)" : "Note (optional)"} value={f.reason} onChange={(e) => set("reason", e.target.value)} />}
        <Button data-testid="resolve-submit" disabled={!action || busy} onClick={submit} className="bg-indigo-600 hover:bg-indigo-700 font-semibold">Apply</Button>
      </DialogContent>
    </Dialog>
  );
}

export default function AssetTransferQueuePage() {
  const [rows, setRows] = useState(null);
  const [active, setActive] = useState(null);
  const load = () => api.get("/asset-transfers/admin/queue").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);
  return (
    <div className="flex flex-col gap-5" data-testid="custody-queue-page">
      <div>
        <h1 className="font-display text-3xl font-black tracking-tight text-slate-900">Custody Queue</h1>
        <p className="text-sm text-slate-500 mt-1">Declined hand-overs and offers unanswered for 7+ days. Nothing changes custody until you act.</p>
      </div>
      {rows === null ? <div className="text-slate-500 text-sm">Loading…</div> : rows.length === 0 ? (
        <div data-testid="custody-queue-empty" className="border-2 border-dashed border-slate-300 rounded p-8 text-center text-slate-500">Nothing needs your attention.</div>
      ) : (
        <div className="bg-white border-2 border-slate-200 rounded-md divide-y">
          {rows.map((r) => (
            <div key={r.id} data-testid={`queue-row-${r.id}`} className="flex flex-col sm:flex-row sm:items-center gap-2 p-3">
              <div className="flex-1 min-w-0">
                <Link to={`/assets/${r.asset?.id}`} className="font-semibold text-slate-900 hover:underline">{r.asset?.asset_number} — {r.asset?.name}</Link>
                <div className="text-xs text-slate-500">{r.from_name} → {r.to_name} · offered {fmtDate(r.created_at)} · {r.days_pending}d</div>
                {r.decline_reason && <div className="text-xs text-red-700 mt-0.5">Declined: {r.decline_reason}</div>}
              </div>
              <span data-testid={`queue-status-${r.id}`} className={`text-xs font-bold uppercase px-2 py-0.5 rounded self-start sm:self-center ${r.status === "declined" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-800"}`}>{r.status === "declined" ? "Declined" : "Overdue"}</span>
              <Button data-testid={`queue-resolve-${r.id}`} onClick={() => setActive(r)} className="h-9 bg-slate-900 hover:bg-slate-800 text-sm font-semibold">Resolve</Button>
            </div>
          ))}
        </div>
      )}
      {active && <ResolveDialog req={active} onClose={() => setActive(null)} onDone={() => { setActive(null); load(); }} />}
    </div>
  );
}
